import PropTypes from "prop-types";
import Form from "../../Form";
import ButtonV2 from "components/Button";
import Grid from "components/primitives/Grid";
import Typography from "@mui/material/Typography";
import { getSchema } from "./validate";
import DuplicateAthleteAlert from "./DuplicateAthleteAlert";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ColorableChip from "components/ColorableChip";
import { grey } from "@mui/material/colors";
import { Field } from "formik";
import { useCallback, useMemo, useState } from "react";
import Mui5 from "components/mui5";
import InputLabelWithOptional from "components/Inputs/InputLabelWithOptional";
import MenuItem from "@mui/material/MenuItem";
import CanDoAction from "components/CanDoAction";
import useRole from "providers/RoleProvider/useRole";
import useCanDo from "hooks/useCanDo";
import FormDatePicker from "components/Form/DatePicker";
import { useTranslation } from "react-i18next";
import FormProfilePhoto from "components/Form/ProfilePhoto";
import AutocompleteOptionColourable from "components/AutocompleteOptionColourable";
import GenderHelpers from "helpers/GenderHelpers";
import AthleteHelpers from "helpers/AthleteHelpers";
import ThemeProvider2024 from "components/MuiThemeProvider/ThemeProvider2024";
import Div from "components/primitives/Div";
import OutputIcon from "components/Icons/OutputIcon";
import FormFieldEmail from "./components/FormFieldEmail";
export { getSchema } from "./validate";

function AthleteForm(props) {
  const [groupsInput, setGroupsInput] = useState("");
  const { groupOptions, initialValues, onSubmit, submitText, ...formikProps } = props;
  const role = useRole();
  const canDo = useCanDo();
  const schema = useMemo(() => getSchema({ groups: { min: role === "OWNER" ? 0 : 1 } }), [role]);
  const { t } = useTranslation();

  /**
   * The email field edits the address the athlete already has, which is their pending change when there is one.
   */
  const formInitialValues = useMemo(
    () => ({ ...initialValues, email: AthleteHelpers.getAthleteEmail(initialValues) }),
    [initialValues]
  );

  const handleSubmit = useCallback(
    (values, formikBag) => {
      onSubmit(schema.cast(values), formikBag);
    },
    [onSubmit, schema]
  );

  return (
    <Form initialValues={formInitialValues} onSubmit={handleSubmit} validationSchema={schema} {...formikProps} disabled>
      <Form.Wrapper>
        <Grid container spacing="16px">
          <Grid size={12} textAlign="center">
            <FormProfilePhoto name="profilePhotoName" buttonText={props.photoChangeButtonText} />
          </Grid>
          <Grid size={12}>
            <Form.Text
              data-sensitive
              fullWidth
              variant="standard"
              color="secondary"
              name="firstName"
              label={t("athlete.form_label_first_name")}
              placeholder={t("athlete.form_placeholder_first_name")}
            />
          </Grid>
          <Grid size={12}>
            <Form.Text
              data-sensitive
              fullWidth
              variant="standard"
              color="secondary"
              name="lastName"
              label={t("athlete.form_label_last_name")}
              placeholder={t("athlete.form_placeholder_last_name")}
            />
            <DuplicateAthleteAlert submitText={submitText} />
          </Grid>
          <Grid size={12}>
            <Form.Text
              fullWidth
              variant="standard"
              color="secondary"
              label={<InputLabelWithOptional optional label={t("athlete.form_label_external_id")} />}
              name="externalId"
            />
          </Grid>
          <Grid size={6}>
            <FormDatePicker
              className="sr-no-capture"
              name="dateOfBirth"
              label={t("athlete.form_label_dob")}
              openTo="year"
              slotProps={{ textField: { color: "secondary" } }}
            />
          </Grid>
          <Grid size={6}>
            <Field name="sex">
              {(sexFieldProps) => {
                return (
                  <Mui5.TextField
                    {...sexFieldProps.field}
                    value={sexFieldProps.field.value}
                    label={t("athlete.form_label_sex")}
                    select
                    placeholder={t("athlete.form_placeholder_sex")}
                    fullWidth
                    variant="standard"
                    color="secondary"
                    aria-label={t("global.gender_picker_aria_label")}
                    error={Boolean(sexFieldProps.meta.touched && sexFieldProps.meta.error)}
                    helperText={sexFieldProps.meta.touched && sexFieldProps.meta.error && t(sexFieldProps.meta.error)}
                    slotProps={{
                      select: {
                        SelectDisplayProps: { "data-sensitive": true },
                      },
                    }}
                  >
                    {GenderHelpers.genderOptions.map((item) => {
                      return (
                        <MenuItem key={item.value} value={item.value} data-sensitive>
                          {t(item.label)}
                        </MenuItem>
                      );
                    })}
                  </Mui5.TextField>
                );
              }}
            </Field>
          </Grid>
          <Grid size={6}>
            <Form.Height
              name="height"
              label={t("athlete.form_label_height")}
              placeholder={t("athlete.form_placeholder_height")}
              variant="standard"
              color="secondary"
              fullWidth
            />
          </Grid>
          <Grid size={6}>
            <Form.Weight
              name="weight"
              label={t("athlete.form_label_weight")}
              placeholder={t("athlete.form_placeholder_weight")}
              variant="standard"
              color="secondary"
              fullWidth
            />
          </Grid>
          <CanDoAction action="group.assign">
            <Grid size={12}>
              <Field name="groups">
                {({ form, field }) => (
                  <Mui5.Autocomplete
                    multiple
                    freeSolo={canDo("group.create")}
                    forcePopupIcon
                    autoHighlight
                    name="groups"
                    options={groupOptions}
                    filterSelectedOptions
                    getOptionLabel={(option) => option.name}
                    value={field.value}
                    onChange={(_, value) => {
                      form.setFieldValue("groups", value);
                      setGroupsInput("");
                    }}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    inputValue={groupsInput}
                    renderInput={(params) => (
                      <Mui5.TextField
                        {...params}
                        slotProps={{
                          inputLabel: {
                            shrink: true,
                          },
                        }}
                        variant="standard"
                        color="secondary"
                        onBlur={() => {
                          const stringValue = groupsInput;
                          if (!stringValue) return;

                          setGroupsInput("");
                          form.setFieldValue("groups", [...field.value, stringValue]);
                        }}
                        error={Boolean(form.touched[field.name] && form.errors[field.name])}
                        onChange={(event) => setGroupsInput(event.target.value)}
                        helperText={
                          form.touched[field.name] && form.errors[field.name]
                            ? t(form.errors[field.name])
                            : params.helperText || (
                                <CanDoAction action="group.create">
                                  {t("athlete.form_unknown_groups_message")}
                                </CanDoAction>
                              )
                        }
                        label={t("athlete.form_label_groups")}
                        placeholder={Boolean(field.value?.length) ? undefined : t("group.search_groups_placeholder")}
                      />
                    )}
                    renderTags={(tagValue, getTagProps) =>
                      tagValue.map((option, index) => {
                        const { key, ...tagProps } = getTagProps({ index });

                        return (
                          <ColorableChip
                            key={option.id || index}
                            label={option.name || option}
                            customColor={typeof option === "string" ? grey[300] : option.colour}
                            deleteIcon={
                              <ClearOutlinedIcon
                                title={t("global.remove_item_aria_label")}
                                color="primary"
                                viewBox="-6 -6 36 36"
                              />
                            }
                            {...tagProps}
                          />
                        );
                      })
                    }
                    renderOption={(optionProps, option) => (
                      <AutocompleteOptionColourable {...optionProps} key={option.id} colour={option.colour}>
                        {option.name}
                      </AutocompleteOptionColourable>
                    )}
                  />
                )}
              </Field>
            </Grid>
          </CanDoAction>
          <ThemeProvider2024>
            <Grid size={12} sx={{ gap: "16px", display: "flex", flexDirection: "column" }}>
              <Div sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                <Div sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                  <OutputIcon />
                  <Typography variant="TextL" sx={{ fontWeight: "fontWeightMedium" }}>
                    {t("athlete.invite_to_capture_title")}
                  </Typography>
                </Div>
                <Typography variant="TextS" sx={{ color: "grey.120" }}>
                  {t("athlete.invite_to_capture_description")}
                </Typography>
              </Div>
              <FormFieldEmail
                email={initialValues.email}
                pendingEmail={initialValues.pendingEmail}
                athleteId={initialValues.id}
              />
            </Grid>
            <Grid size={12}>
              <ButtonV2 type="submit" disabled={props.submitDisabled || initialValues.isArchived} fullWidth>
                {submitText}
              </ButtonV2>
            </Grid>
          </ThemeProvider2024>
        </Grid>
      </Form.Wrapper>
    </Form>
  );
}

AthleteForm.propTypes = {
  groupOptions: PropTypes.arrayOf(PropTypes.instanceOf(Object).isRequired).isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitText: PropTypes.string.isRequired,
  initialValues: PropTypes.shape({
    /** Absent when creating an athlete, since they do not exist yet. */
    id: PropTypes.string,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    externalId: PropTypes.string,
    email: PropTypes.string,
    pendingEmail: PropTypes.string,
    pendingEmailAddedAt: PropTypes.string,
    fileToUpload: PropTypes.instanceOf(Object),
    dateOfBirth: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
    sex: PropTypes.string,
    groups: PropTypes.arrayOf(PropTypes.instanceOf(Object).isRequired).isRequired,
    height: PropTypes.number,
    weight: PropTypes.number,
  }).isRequired,
  currentPhotoUrl: PropTypes.string,
  submitDisabled: PropTypes.bool,
};

export default AthleteForm;
